import { Wrench, Truck, AlertTriangle, MapPinned, Globe, Warehouse } from "lucide-react";
import { SERVICES } from "@/lib/brand";

const ICONS = {
  recovery: Wrench,
  transport: Truck,
  accident: AlertTriangle,
  nationwide: MapPinned,
  european: Globe,
  storage: Warehouse,
};

const IMAGES = {
  recovery:
    "https://customer-assets.emergentagent.com/job_transport-hub-345/artifacts/gwof54c1_Screenshot_20260609_112326_Gallery.jpg",
  transport:
    "https://customer-assets.emergentagent.com/job_transport-hub-345/artifacts/t368qocj_Screenshot_20260609_112821_Gallery.jpg",
  european:
    "https://customer-assets.emergentagent.com/job_transport-hub-345/artifacts/5qdx1f86_Screenshot_20260609_112831_Gallery.jpg",
};

export default function Services() {
  return (
    <section id="services" data-testid="services-section" className="relative bg-[#050505] py-24 md:py-32">
      <div className="max-w-[1500px] mx-auto px-6 md:px-12">
        {/* Heading */}
        <div className="grid lg:grid-cols-12 gap-8 lg:gap-16 items-end mb-16">
          <div className="lg:col-span-7">
            <div className="flex items-center gap-3 text-[11px] tracking-[0.3em] uppercase text-[#E60000] font-bold mb-6">
              <span className="w-8 h-px bg-[#E60000]" />
              Services · 01
            </div>
            <h2 className="font-display font-extrabold text-white text-4xl sm:text-5xl lg:text-7xl tracking-tight leading-[1.0]">
              Every mile,
              <br />
              <span className="text-zinc-500">covered.</span>
            </h2>
          </div>
          <div className="lg:col-span-5">
            <p className="text-zinc-400 text-base lg:text-lg leading-relaxed">
              From late-night roadside callouts to enclosed cross-channel
              transport, MHJ Prestige operates a single playbook — care,
              speed and transparency on every job.
            </p>
          </div>
        </div>

        {/* Bento grid */}
        <div className="grid grid-cols-1 md:grid-cols-6 gap-px bg-white/10 border border-white/10">
          {/* Recovery — large */}
          <ServiceCard
            service={SERVICES[0]}
            image={IMAGES.recovery}
            className="md:col-span-4 md:row-span-2 min-h-[420px]"
            featured
          />
          {/* Transport */}
          <ServiceCard
            service={SERVICES[1]}
            image={IMAGES.transport}
            className="md:col-span-2 min-h-[260px]"
          />
          {/* Accident */}
          <ServiceCard
            service={SERVICES[2]}
            className="md:col-span-2 min-h-[260px]"
          />
          {/* Nationwide */}
          <ServiceCard
            service={SERVICES[3]}
            className="md:col-span-2 min-h-[260px]"
          />
          {/* European */}
          <ServiceCard
            service={SERVICES[4]}
            image={IMAGES.european}
            className="md:col-span-2 min-h-[260px]"
          />
          {/* Storage */}
          <ServiceCard
            service={SERVICES[5]}
            className="md:col-span-2 min-h-[260px]"
          />
        </div>
      </div>
    </section>
  );
}

function ServiceCard({ service, image, className = "", featured = false }) {
  const Icon = ICONS[service.id] || Truck;

  return (
    <div
      data-testid={`service-card-${service.id}`}
      className={`group relative bg-[#0A0A0A] hover:bg-[#0F0F0F] transition-colors overflow-hidden ${className}`}
    >
      {image && (
        <>
          <img
            src={image}
            alt={service.title}
            className="absolute inset-0 w-full h-full object-cover opacity-50 group-hover:opacity-75 group-hover:scale-105 transition-all duration-700"
            loading="lazy"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black via-black/70 to-black/20" />
        </>
      )}

      <div className="relative h-full p-8 lg:p-10 flex flex-col justify-between">
        <div className="flex items-start justify-between">
          <div className="border border-white/15 p-3">
            <Icon className="w-5 h-5 text-[#E60000]" strokeWidth={1.8} />
          </div>
          <span className="text-[10px] tracking-[0.25em] uppercase text-zinc-500 font-bold">
            {service.tag}
          </span>
        </div>

        <div>
          <h3
            className={`font-display font-bold text-white tracking-tight ${
              featured ? "text-4xl lg:text-6xl" : "text-2xl lg:text-3xl"
            }`}
          >
            {service.title}
          </h3>
          <p className="mt-4 text-zinc-400 text-sm lg:text-base max-w-md leading-relaxed">
            {service.blurb}
          </p>

          {featured && (
            <ul className="mt-6 grid grid-cols-1 sm:grid-cols-3 gap-3">
              {service.points.map((p, i) => (
                <li
                  key={i}
                  className="flex items-start gap-2 text-xs lg:text-sm text-zinc-300"
                >
                  <span className="text-[#E60000] mt-1">·</span>
                  {p}
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}
